import React, { useState } from "react";
import Swal from "sweetalert2";
import "../css/contact.css";

export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const validateForm = (formData) => {
    const errors = {};
    const name = formData.get('name');
    const email = formData.get('email');
    const message = formData.get('message');

    if (!name || name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!message || message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    }

    return errors;
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    const formData = new FormData(event.target);

    // Check honeypot field (spam prevention)
    if (formData.get('honeypot')) {
      console.warn('Spam detected');
      return;
    }

    // Validate form
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    setIsSubmitting(true);

    // Get API key from environment variable
    const apiKey = process.env.REACT_APP_WEB3FORMS_KEY;

    if (!apiKey) {
      Swal.fire({
        title: "Configuration Error",
        text: "Contact form is not properly configured. Please contact me directly via social media.",
        icon: "error",
      });
      setIsSubmitting(false);
      return;
    }

    formData.append("access_key", apiKey);

    const object = Object.fromEntries(formData);
    const json = JSON.stringify(object);

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: json,
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();

      if (data.success) {
        await Swal.fire({
          title: "Thank you for getting in touch!",
          text: "Your message has been successfully sent. I'll get back to you shortly.",
          icon: "success",
          confirmButtonColor: "var(--primary-color)",
        });
        event.target.reset();
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      Swal.fire({
        title: "Oops!",
        text: "Failed to send message. Please try again or contact me directly via email or social media.",
        icon: "error",
        confirmButtonColor: "var(--primary-color)",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-form-container">
      <form className="form" onSubmit={onSubmit}>
        {/* Honeypot field for spam prevention */}
        <input
          type="text"
          name="honeypot"
          style={{ display: 'none' }}
          tabIndex="-1"
          autoComplete="off"
        />

        <div className="form-group">
          <label htmlFor="name">Name</label>
          <input
            className={`form-input ${formErrors.name ? 'error' : ''}`}
            type="text"
            name="name"
            id="name"
            placeholder="Enter your name"
            autoComplete="name"
            required
            disabled={isSubmitting}
          />
          {formErrors.name && (
            <span className="error-message">{formErrors.name}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            className={`form-input ${formErrors.email ? 'error' : ''}`}
            type="email"
            name="email"
            id="email"
            placeholder="Enter your email"
            autoComplete="email"
            required
            disabled={isSubmitting}
          />
          {formErrors.email && (
            <span className="error-message">{formErrors.email}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="message">Message</label>
          <textarea
            className={`form-input ${formErrors.message ? 'error' : ''}`}
            name="message"
            id="message"
            rows="5"
            placeholder="Your message"
            autoComplete="off"
            required
            disabled={isSubmitting}
          ></textarea>
          {formErrors.message && (
            <span className="error-message">{formErrors.message}</span>
          )}
        </div>

        <button
          className="btn btn-submit"
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-small"></span>
              Sending...
            </>
          ) : (
            'Send Message'
          )}
        </button>
      </form>
    </div>
  );
}
